import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { BooksFees, BookLines } from '../../components/BooksFees';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { CreditCard, Loader2, CheckCircle, ExternalLink } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
    const { user } = useAuth();
    const [fees, setFees] = useState(null);
    const [payments, setPayments] = useState([]);
    const location = useLocation();
    const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') === 'books' ? 'books' : 'fees');
    useEffect(() => { if (new URLSearchParams(location.search).get('tab') === 'books') setTab('books'); }, [location.search]);
    const [booksPending, setBooksPending] = useState(0);
    const bookPayments = payments.filter(p => p.payment_type === 'books');
    const schoolPayments = payments.filter(p => p.payment_type !== 'books');
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [payAmount, setPayAmount] = useState('');

    useEffect(() => {
        fetchData();
        // Check for payment success
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        }
    }, [user]);

    const fetchData = async () => {
        try {
            const effectiveId = user?.linked_parent_id || user?.id;
            const [feesRes, paymentsRes] = await Promise.all([
                axios.get(`${API_URL}/api/parents/${effectiveId}/fees`, { withCredentials: true }),
                axios.get(`${API_URL}/api/payments`, { withCredentials: true })
            ]);
            setFees(feesRes.data);
            // Exclude activity receipts from the parent "School Fees" payment history
            // (activity receipts have their own view inside Parent → Activities → Participated).
            setPayments((paymentsRes.data || []).filter(p => p.payment_type !== 'activity'));
            setPayAmount((feesRes.data?.remaining || 0).toFixed(2));
        } catch (error) {
            toast.error('Failed to load payment data');
        } finally {
            setLoading(false);
        }
    };

    // Reconcile any pending Stripe sessions for this parent in case polling timed out last visit
    const reconcilePending = async () => {
        try {
            const res = await axios.post(`${API_URL}/api/payments/reconcile-pending`, {}, { withCredentials: true });
            if (res.data?.finalized > 0) {
                toast.success(`Receipt issued for ${res.data.finalized} previously pending payment${res.data.finalized > 1 ? 's' : ''}`);
                fetchData();
            }
        } catch (_) { /* silent — pending reconcile happens opportunistically and rarely errors meaningfully */
            console.debug('reconcilePending skipped:', _?.message);
        }
    };

    useEffect(() => {
        if (user?.id && !loading) reconcilePending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, loading]);

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 15; // ~45 seconds
        if (attempts >= maxAttempts) {
            toast.info('Still processing your payment. Refresh in a moment — your receipt will appear here.');
            fetchData(); // show any already-recorded receipt
            return;
        }

        try {
            const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
            if (response.data.payment_status === 'paid') {
                toast.success('Payment successful! Receipt issued.');
                window.history.replaceState({}, '', window.location.pathname);
                fetchData();
            } else if (response.data.status === 'expired') {
                toast.error('Payment session expired');
                fetchData();
            } else {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Error checking payment status:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
        }
    };

    const handlePayTuition = async () => {
        const amt = parseFloat(payAmount);
        const rem = Number(fees?.remaining || 0);
        if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
        if (amt > rem + 0.01) { toast.error(`Amount cannot exceed remaining balance (€${rem.toFixed(2)})`); return; }
        setPaying(true);
        try {
            const response = await axios.post(`${API_URL}/api/payments/checkout/tuition`, { amount: amt }, { withCredentials: true });
            window.location.href = response.data.url;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to initiate payment');
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="payments-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Fees & Payments</h1>
                    <p className="text-[#4B5563]">View your fees and make payments</p>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="space-y-6">
                    <TabsList data-testid="payments-tabs"><TabsTrigger value="fees" data-testid="tab-school-fees">School fees</TabsTrigger><TabsTrigger value="books" data-testid="tab-books">Books{booksPending > 0 && <Badge className="ml-2 bg-[#B45309]">{booksPending}</Badge>}</TabsTrigger></TabsList>
                    <TabsContent value="fees" className="space-y-6">
                {/* Fee Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <p className="text-sm text-[#4B5563] mb-1">Number of Students</p>
                            <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{fees?.num_students || 0}</p>
                        </CardContent>
                    </Card>
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <p className="text-sm text-[#4B5563] mb-1">School Fees</p>
                            <p className="text-3xl font-bold text-[#1F2937] tabular-nums" data-testid="fees-school">€{fees?.total_fees?.toFixed(2) || '0.00'}</p>
                        </CardContent>
                    </Card>
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <p className="text-sm text-[#4B5563] mb-1">Previous Debt</p>
                            <p className="text-3xl font-bold text-[#F59E0B] tabular-nums" data-testid="fees-previous-debt">€{fees?.previous_debt?.toFixed(2) || '0.00'}</p>
                        </CardContent>
                    </Card>
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <p className="text-sm text-[#4B5563] mb-1">Discount</p>
                            <p className="text-3xl font-bold text-[#10B981] tabular-nums">-€{fees?.discount?.toFixed(2) || '0.00'}</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Payment Summary Card */}
                <Card className="card-surface">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center justify-between md:gap-12">
                                    <span className="text-[#4B5563]">Total Due <span className="text-xs">(school fees + previous debt − discount)</span></span>
                                    <span className="text-2xl font-bold text-[#1F2937] tabular-nums" data-testid="fees-total-due">€{fees?.total_due?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="flex items-center justify-between md:gap-12">
                                    <span className="text-[#4B5563]">Total Paid</span>
                                    <span className="text-2xl font-bold text-[#10B981] tabular-nums" data-testid="fees-total-paid">€{fees?.total_paid?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="h-px bg-[#E5E7EB]" />
                                <div className="flex items-center justify-between md:gap-12">
                                    <span className="font-semibold text-[#1F2937]">Remaining Balance</span>
                                    <span className={`text-3xl font-bold tabular-nums ${fees?.remaining > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid="fees-remaining">
                                        €{fees?.remaining?.toFixed(2) || '0.00'}
                                    </span>
                                </div>
                                {fees?.books_unpaid > 0 && <p className="text-xs text-[#B45309]" data-testid="fees-remaining-breakdown">Books are paid separately — €{fees.books_unpaid.toFixed(2)} due in the Books tab</p>}
                            </div>
                            <div className="flex flex-col items-stretch md:min-w-[260px]">
                                {fees?.remaining > 0 ? (
                                    <>
                                        <Label className="text-[#4B5563] mb-1">School fees — amount to pay ({'\u20AC'})</Label>
                                        <div className="flex gap-2 mb-2">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={fees?.remaining}
                                                value={payAmount}
                                                onChange={e => setPayAmount(e.target.value)}
                                                className="text-lg font-semibold tabular-nums"
                                                data-testid="partial-amount-input"
                                            />
                                            <Button type="button" variant="outline" size="sm" onClick={() => setPayAmount((Number(fees?.remaining || 0)).toFixed(2))} data-testid="pay-full-btn">Full</Button>
                                        </div>
                                        <Button
                                            onClick={handlePayTuition}
                                            disabled={paying}
                                            className="bg-[#064E3B] hover:bg-[#047857] h-12 px-8 text-lg"
                                            data-testid="pay-now-btn"
                                        >
                                            {paying ? (
                                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                            ) : (
                                                <CreditCard className="h-5 w-5 mr-2" />
                                            )}
                                            Pay Now
                                        </Button>
                                        <p className="text-xs text-[#4B5563] mt-2 flex items-center justify-center">
                                            <ExternalLink className="h-3 w-3 mr-1" />
                                            Secure payment via Stripe — you can pay a partial amount
                                        </p>
                                    </>
                                ) : (
                                    <div className="flex items-center text-[#10B981]">
                                        <CheckCircle className="h-6 w-6 mr-2" />
                                        <span className="font-semibold">School fees all paid!</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Payment History */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit']">Payment History</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {schoolPayments.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Receipt #</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Method</TableHead>
                                            <TableHead>Issued by</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Amount</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {schoolPayments.map((payment) => (
                                            <TableRow key={payment.id}>
                                                <TableCell className="font-mono">{payment.receipt_number}</TableCell>
                                                <TableCell className="tabular-nums">
                                                    {formatDateTime(payment.created_at)}
                                                </TableCell>
                                                <TableCell className="capitalize">{payment.payment_type === 'books' ? <Badge className="bg-[#B45309]">Books</Badge> : payment.payment_type}<BookLines payment={payment} /></TableCell>
                                                <TableCell className="capitalize">{(payment.method || '').replace('_', ' ')}</TableCell>
                                                <TableCell className="text-sm text-[#4B5563]">{payment.recorded_by_name || (payment.method === 'stripe' ? 'Auto System' : 'School Staff')}</TableCell>
                                                <TableCell>
                                                    <Badge className={payment.status === 'completed' ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}>
                                                        {payment.status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-semibold tabular-nums">
                                                    €{payment.amount?.toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <CreditCard className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563]">No payment history yet</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
                    </TabsContent>
                    <TabsContent value="books" className="space-y-6">
                        <BooksFees parentId={user?.linked_parent_id || user?.id} onLoaded={(d) => setBooksPending(d?.proposed_count || 0)} />
                        <Card className="card-surface"><CardHeader><CardTitle className="font-['Outfit']">Books receipts</CardTitle></CardHeader>
                            <CardContent className="p-0">{bookPayments.length === 0 ? <p className="p-6 text-sm text-[#6B7280] text-center" data-testid="books-receipts-empty">No books receipts yet.</p> : <Table><TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Date</TableHead><TableHead>Books</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                                <TableBody>{bookPayments.map(p => <TableRow key={p.id} data-testid={`books-receipt-${p.id}`}><TableCell className="font-mono text-sm">{p.receipt_number}</TableCell><TableCell>{String(p.created_at).slice(0, 10)}</TableCell><TableCell><BookLines payment={p} /></TableCell><TableCell className="capitalize">{p.method}</TableCell><TableCell className="text-right tabular-nums font-semibold">€{Number(p.amount).toFixed(2)}</TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};

export default Payments;
